import React from "react";
import applicationImg from "../assets/application.webp";
import interviewImg from "../assets/interview.png";
import morganImg from "../assets/morgan.png";
import nvidiaImg from "../assets/nvidia.webp";

const FeatureCard = ({ title, description, comparisons, image }) => {
  return (
    <div className="flex flex-col gap-6 border border-[#0000000F] rounded-2xl p-4 lg:p-8">
      {image && <img src={image} alt={title} className="object-contain mb-2" />}
      <h3 className="text-2xl font-bold font-public">{title}</h3>
      <p className="text-gray-600">{description}</p>
      <div>
        <p className="text-sm font-medium mb-3">COMPARED TO MANUAL PROCESS:</p>
        <div className="flex flex-wrap gap-3">
          {comparisons.map((comparison, index) => (
            <div
              key={index}
              style={{
                backgroundColor:
                  comparison.type === "green"
                    ? "rgba(142, 223, 55, 0.16)"
                    : comparison.type === "yellow"
                    ? "rgba(224, 210, 13, 0.16)"
                    : comparison.type === "purple"
                    ? "rgba(125, 55, 223, 0.1)"
                    : "rgba(55, 187, 223, 0.16)",
                fontSize: "16px",
                lineHeight: "22px",
                letterSpacing: "0%",
              }}
              className="py-2 px-4 rounded-full text-black font-medium flex items-center gap-2"
            >
              <div
                className="w-2 h-2 rounded-full"
                style={{
                  backgroundColor:
                    comparison.type === "green"
                      ? "rgb(142, 223, 55)"
                      : comparison.type === "yellow"
                      ? "rgb(224, 210, 13)"
                      : comparison.type === "purple"
                      ? "rgb(125, 55, 223)"
                      : "rgb(55, 187, 223)",
                }}
              />
              {comparison.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const FeaturesSection = ({
  onFirstButtonClick,
  onSecondButtonClick,
  showFirstButton,
  showSecondButton,
  firstButtonText,
  secondButtonText,
  customfeatures,
  title,
  subtitle,
  modifiers = /** @type {{ badgeDotClass?: string }} */ ({}),
}) => {
  const { badgeDotClass = 'bg-green-500' } = modifiers;
  const features = [
    {
      title: "Get matched with the highest-paying jobs you’re most likely to land",
      description:
        "In seconds, we scan thousands of job boards and career pages to find open opportunities that match your salary goals, experience, and other preferences.",
      comparisons: [
        { text: "Find 20x more relevant jobs", type: "green" },
        { text: "Cut search time by 90%", type: "green" },
      ],
      image: nvidiaImg,
    },
    {
      title: "We build your resume and tailor it perfectly for every application",
      description:
        "Our system builds you a professional-grade resume and personalizes it for any job — perfectly matching the role’s requirements and maximizing your chances of landing an interview.",
      comparisons: [
        { text: "Get 10x more interviews", type: "blue" },
        { text: "Land interviews 5x faster", type: "blue" },
      ],
      image: morganImg,
    },
    {
      title: "Auto-apply to countless jobs instantly",
      description:
        "Effortlessly auto-apply to tons of companies with one click and save hours of busywork—all tracked and managed in an automated dashboard.",
      comparisons: [
        { text: "Send 50x more applications", type: "purple" },
        { text: "Cut application time by 95%", type: "purple" },
      ],
      image: applicationImg,
    },
    {
      title: "Ace any interview and negotiation",
      description:
        "Prepare with a personalized coach—every simulation is tailored to the exact job you’re applying for. Get real-time feedback, personalized answer suggestions, and a custom cheat sheet.",
      comparisons: [
        { text: "Get 5x more job offers", type: "yellow" },
        { text: "Get the highest offer possible", type: "yellow" },
      ],
      image: interviewImg,
    },
  ];
  
  const defaultFeatures = customfeatures || features;

  return (
    <>
      <div className="bg-white py-16 pb-8 lg:py-24" id="features">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20">
          {/* Header */}
          <div className="text-left sm:text-center sm:mb-16 mb-8">
            <div className="inline-flex items-center gap-2 mb-4  border border-[#0000000F] px-4 py-2 rounded-lg">
              <div className={`w-2 h-2 rounded-full ${badgeDotClass}`}></div>
              <span className="text-sm font-medium">Features</span>
            </div>
            <h2 className="text-[32px] md:text-[48px] lg:text-5xl font-semibold font-public mb-[16px] md:max-w-[80%] md:m-auto md:mb-[16px]">
              {title || "Get job offers while you focus on your current job"}
            </h2>
            <p className="font-inter font-normal text-[18px] leading-[26px] md:max-w-[80%] md:m-auto">
            {subtitle || "We automate job hunting and help you at every step — so you can land higher-paying offers with minimal time and effort"}
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-10 mb-12">
            {defaultFeatures.map((feature, index) => (
              <FeatureCard key={index} {...feature} />
            ))}
          </div>
          <div className="flex flex-col items-center mt-8 gap-6">
          <div className="flex justify-center px-4 w-full flex-col md:flex-row gap-4">
            {showFirstButton && (
            <button
              onClick={onFirstButtonClick}
              className="bg-black text-white flex justify-center items-center gap-[10px] px-6 py-4 rounded-[100px] font-semibold transition-colors w-full sm:w-auto hover:bg-opacity-90"
            >
              {firstButtonText}
            </button>
            )}
            {showSecondButton && (
            <button
                onClick={onSecondButtonClick}
                className="bg-[#EBEBEB] text-black flex justify-center items-center gap-[10px] px-6 py-4 rounded-[100px] font-semibold transition-colors w-full sm:w-auto hover:bg-gray-200"
              >
                {secondButtonText}
              </button>
            )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FeaturesSection;
